﻿import { useState, useEffect, useRef } from 'react';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { ArrowRightLeft, Download, Eye, Pencil, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { PERMISSIONS } from '@pms/shared';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel,
  AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { assetsService, type AssetDetail, type AssetListItem } from '@/services/assets.service';
import { departmentsService } from '@/services/departments.service';
import { usersService } from '@/services/users.service';
import { PermissionGate } from '@/components/guards/PermissionGate';
import { getErrorMessage } from '@/lib/errors';
import { DataTable, type Column } from '@/components/ui/data-table';
import { Button } from '@/components/ui/button';
import { SearchInput } from '@/components/ui/search-input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { AssetStatusDropdown } from './components/AssetStatusDropdown';
import { CreateAssetModal } from './components/CreateAssetModal';
import { EditAssetModal } from './components/EditAssetModal';
import { AssetDetailDrawer } from './components/AssetDetailDrawer';
import { AssetFilesDialog } from './components/AssetFilesDialog';
import {
  AssetBrandModelCell,
  AssetCategoryCell,
  AssetCodeCell,
  AssetFilesCell,
  AssetWarrantyCell,
} from './components/AssetTableCells';
import { ASSET_COL } from './components/assetTable';
import { TransferAssetDialog } from './components/TransferAssetDialog';

export function AssetsListPage() {
  const queryClient = useQueryClient();

  // Pagination
  const [page, setPage] = useState(1);
  const limit = 15;

  // Filters
  const [q, setQ] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [statusId, setStatusId] = useState('');
  const [departmentId, setDepartmentId] = useState('');
  const [userId, setUserId] = useState('');

  // Debounce search
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDebouncedQ(q);
      setPage(1);
    }, 350);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [q]);

  // Dialog state
  const [createOpen, setCreateOpen] = useState(false);
  const [editAsset, setEditAsset] = useState<AssetDetail | null>(null);
  const [viewAssetId, setViewAssetId] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<AssetListItem | null>(null);
  const [filesAsset, setFilesAsset] = useState<AssetListItem | null>(null);
  const [transferTarget, setTransferTarget] = useState<AssetListItem | null>(null);

  const deleteMutation = useMutation({
    mutationFn: (id: string) => assetsService.deleteAsset(id),
    onSuccess: () => {
      toast.success('Asset deleted');
      setDeleteTarget(null);
      void queryClient.invalidateQueries({ queryKey: ['assets', 'list'] });
      void queryClient.invalidateQueries({ queryKey: ['assets', 'stats'] });
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  // Build query params
  const params = {
    page,
    limit,
    q: debouncedQ || undefined,
    category_id: categoryId || undefined,
    status_id: statusId || undefined,
    department_id: departmentId || undefined,
    assigned_to_user_id: userId || undefined,
  };

  // Export — sends the same filters as the list; backend returns every match
  const [exporting, setExporting] = useState(false);

  const handleExport = async () => {
    setExporting(true);
    try {
      await assetsService.exportCsv(params);
    } catch (e) {
      toast.error(getErrorMessage(e));
    } finally {
      setExporting(false);
    }
  };

  // Queries
  const { data: assetsData, isLoading: assetsLoading, isError: assetsError } = useQuery({
    queryKey: ['assets', 'list', params],
    queryFn: () => assetsService.listAssets(params),
  });

  const { data: statuses = [] } = useQuery({
    queryKey: ['assets', 'statuses'],
    queryFn: () => assetsService.listStatuses(),
  });

  const { data: categories = [] } = useQuery({
    queryKey: ['assets', 'categories'],
    queryFn: () => assetsService.listCategories(),
  });

  const { data: deptsData } = useQuery({
    queryKey: ['departments'],
    queryFn: () => departmentsService.list({ limit: 100 }),
  });

  const { data: users = [] } = useQuery({
    queryKey: ['users', 'active-all'],
    queryFn: () => usersService.listAllActive(),
  });

  const updateFilter = (setter: (v: string) => void) => (v: string) => {
    setter(v === '__all__' ? '' : v);
    setPage(1);
  };

  // Handle edit — fetch full detail
  const handleEdit = async (assetId: string) => {
    try {
      setEditAsset(await assetsService.getAsset(assetId));
    } catch (e) {
      toast.error(getErrorMessage(e));
    }
  };

  const columns: Column<AssetListItem>[] = [
    {
      key: 'photo',
      header: 'Photo / Bill',
      className: ASSET_COL.files,
      cell: (row) => (
        <AssetFilesCell row={row} documentBasePath="/assets/documents" onOpen={() => setFilesAsset(row)} />
      ),
    },
    { key: 'asset_code', header: 'Asset Code', className: ASSET_COL.code, cell: (row) => <AssetCodeCell row={row} /> },
    { key: 'category', header: 'Category', className: ASSET_COL.category, cell: (row) => <AssetCategoryCell row={row} /> },
    { key: 'brand_model', header: 'Brand / Model', className: ASSET_COL.brandModel, cell: (row) => <AssetBrandModelCell row={row} /> },
    {
      key: 'status',
      header: 'Status',
      className: ASSET_COL.status,
      cell: (row) =>
        row.status ? (
          <AssetStatusDropdown
            assetId={row.id}
            currentStatusId={row.status.id}
            currentStatusName={row.status.name}
            currentStatusColor={row.status.color}
            statuses={statuses}
          />
        ) : (
          <span className="text-muted-foreground text-sm">—</span>
        ),
    },
    {
      key: 'assigned_to',
      header: 'Assigned To',
      className: ASSET_COL.assignedTo,
      cell: (row) =>
        row.assigned_to ? (
          <button
            type="button"
            onClick={() => { setUserId(row.assigned_to!.id); setPage(1); }}
            className="text-sm text-primary hover:underline text-left"
          >
            {row.assigned_to.name}
          </button>
        ) : (
          <span className="text-muted-foreground text-sm">—</span>
        ),
    },
    { key: 'warranty', header: 'Warranty', className: ASSET_COL.warranty, cell: (row) => <AssetWarrantyCell row={row} /> },
    {
      key: 'actions',
      header: 'Action',
      className: ASSET_COL.actions,
      cell: (row) => (
        <div className="flex items-center justify-end gap-0.5">
          <button
            type="button"
            onClick={() => setViewAssetId(row.id)}
            className="rounded p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
            aria-label="View details"
          >
            <Eye className="h-4 w-4" />
          </button>
          <PermissionGate permission={PERMISSIONS.ASSETS_EDIT}>
            <button
              type="button"
              onClick={() => void handleEdit(row.id)}
              className="rounded p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
              aria-label="Edit asset"
            >
              <Pencil className="h-4 w-4" />
            </button>
          </PermissionGate>
          <PermissionGate permission={PERMISSIONS.ASSETS_TRANSFER}>
            <button
              type="button"
              onClick={() => setTransferTarget(row)}
              className="rounded p-1.5 text-muted-foreground hover:bg-primary/10 hover:text-primary"
              aria-label="Transfer to branch"
              title="Transfer to branch"
            >
              <ArrowRightLeft className="h-4 w-4" />
            </button>
          </PermissionGate>
          <PermissionGate permission={PERMISSIONS.ASSETS_DELETE}>
            <button
              type="button"
              onClick={() => setDeleteTarget(row)}
              className="rounded p-1.5 text-muted-foreground hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/20 dark:hover:text-red-400"
              aria-label="Delete asset"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </PermissionGate>
        </div>
      ),
    },
  ];

  const pagination = assetsData
    ? {
        page: assetsData.pagination.page,
        totalPages: assetsData.pagination.totalPages,
        total: assetsData.pagination.total,
        onPageChange: (p: number) => setPage(p),
      }
    : undefined;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Assets</h1>
          <p className="text-sm text-muted-foreground">
            Track and manage organizational assets
          </p>
        </div>
        <div className="flex items-center gap-2">
          <PermissionGate permission={PERMISSIONS.ASSETS_EXPORT}>
            <Button
              variant="outline"
              onClick={() => void handleExport()}
              disabled={exporting}
            >
              <Download className="h-4 w-4" /> {exporting ? 'Exporting…' : 'Export'}
            </Button>
          </PermissionGate>
          <PermissionGate permission={PERMISSIONS.ASSETS_CREATE}>
            <Button onClick={() => setCreateOpen(true)}>
              <Plus className="h-4 w-4" /> Add Asset
            </Button>
          </PermissionGate>
        </div>
      </div>

      {/* Filter bar */}
      <div className="flex flex-wrap items-center gap-2">
        {/* Search */}
        <SearchInput
          value={q}
          onChange={setQ}
          placeholder="Search code, category, brand, model, user…"
          containerClassName="min-w-[220px] flex-1 sm:max-w-sm"
        />

        {/* Category filter */}
        <Select
          value={categoryId || '__all__'}
          onValueChange={updateFilter(setCategoryId)}
        >
          <SelectTrigger className="h-9 w-44">
            <SelectValue placeholder="All Categories" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">All Categories</SelectItem>
            {categories.map((c) => (
              <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Status filter */}
        <Select
          value={statusId || '__all__'}
          onValueChange={updateFilter(setStatusId)}
        >
          <SelectTrigger className="h-9 w-40">
            <SelectValue placeholder="All Statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">All Statuses</SelectItem>
            {statuses.map((s) => (
              <SelectItem key={s.id} value={s.id}>
                <span className="flex items-center gap-2">
                  <span
                    className="h-2 w-2 rounded-full shrink-0"
                    style={{ backgroundColor: s.color }}
                  />
                  {s.name}
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Department filter */}
        <Select
          value={departmentId || '__all__'}
          onValueChange={updateFilter(setDepartmentId)}
        >
          <SelectTrigger className="h-9 w-44">
            <SelectValue placeholder="All Departments" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">All Departments</SelectItem>
            {(deptsData?.data ?? []).map((d) => (
              <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Assigned To filter */}
        <Select
          value={userId || '__all__'}
          onValueChange={updateFilter(setUserId)}
        >
          <SelectTrigger className="h-9 w-44">
            <SelectValue placeholder="All Users" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">All Users</SelectItem>
            {users.map((u) => (
              <SelectItem key={u.id} value={u.id}>
                {u.first_name} {u.last_name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {assetsData && (
          <span className="ml-auto text-xs text-muted-foreground">
            {assetsData.pagination.total}{' '}
            {assetsData.pagination.total === 1 ? 'asset' : 'assets'}
          </span>
        )}
      </div>

      {/* Table */}
      <DataTable
        columns={columns}
        rows={assetsData?.data ?? []}
        rowKey={(row) => row.id}
        loading={assetsLoading}
        error={assetsError}
        onRetry={() => void queryClient.invalidateQueries({ queryKey: ['assets', 'list'] })}
        empty={debouncedQ ? `No assets match "${debouncedQ}"` : 'No assets match the current filters'}
        pagination={pagination}
      />

      {/* Modals */}
      <CreateAssetModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        statuses={statuses}
        categories={categories}
      />

      <EditAssetModal
        open={!!editAsset}
        onClose={() => setEditAsset(null)}
        asset={editAsset}
        statuses={statuses}
        categories={categories}
      />

      <AssetDetailDrawer
        assetId={viewAssetId}
        onClose={() => setViewAssetId(null)}
      />

      <AssetFilesDialog asset={filesAsset} onClose={() => setFilesAsset(null)} />

      <TransferAssetDialog
        key={transferTarget?.id ?? 'none'}
        asset={transferTarget}
        onClose={() => setTransferTarget(null)}
      />

      <AlertDialog open={!!deleteTarget} onOpenChange={(v) => !v && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Asset</AlertDialogTitle>
            <AlertDialogDescription>
              Delete asset <strong>{deleteTarget?.asset_code}</strong>? This will deactivate
              the asset record. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteMutation.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={deleteMutation.isPending}
              onClick={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
            >
              {deleteMutation.isPending ? 'Deleting…' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
